import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';

interface SEOProps {
  titleKey?: string;
  descriptionKey?: string;
  keywordsKey?: string;
  // Raw string overrides (used for dynamic pages — teacher/article — where data isn't in translations)
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  noindex?: boolean;
  structuredData?: object;
}

const SEO = ({
  titleKey = 'seo.home.title',
  descriptionKey = 'seo.home.description',
  keywordsKey = 'seo.home.keywords',
  title: titleProp,
  description: descriptionProp,
  keywords: keywordsProp,
  image = '/og-image.jpg',
  url,
  type = 'website',
  noindex = false,
  structuredData,
}: SEOProps) => {
  const { t, language } = useLanguage();

  const title = titleProp ?? t(titleKey);
  const description = descriptionProp ?? t(descriptionKey);
  const keywords = keywordsProp ?? t(keywordsKey);
  const siteName = t('seo.siteName');
  // Full URL (with query) — used for og:url / twitter:url (acceptable for social sharing)
  // Same fixed-origin rule as baseUrl below — location.href would bake the
  // prerender host into og:url/twitter:url. Keep path + query (?lang is fine
  // for social URLs; only the canonical must be query-stripped).
  const currentUrl =
    url ||
    (typeof window !== 'undefined'
      ? `https://hudanlilmuttaqeen.com${window.location.pathname}${window.location.search}`
      : 'https://hudanlilmuttaqeen.com');

  // Determine alternate language URL
  // Fixed production origin — window.location.origin would bake the
  // prerender server's host (127.0.0.1:4173) into the static HTML.
  const baseUrl = 'https://hudanlilmuttaqeen.com';
  // Unfurlers (WhatsApp, Facebook, LinkedIn, Slack) need an absolute image URL.
  const imageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  // Canonical URL must NOT include query params (e.g. ?lang=ar) to avoid duplicate-content signals
  const canonicalUrl = url || `${baseUrl}${currentPath}`;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      {/* NOTE: lang/dir on <html> are set authoritatively by LanguageContext
          via useLayoutEffect. We do NOT set them here via Helmet because
          react-helmet-async defers attribute updates via requestAnimationFrame,
          which can race with Playwright's networkidle check on pages that
          trigger data-load re-renders (e.g. FAQ). LanguageContext owns
          document.documentElement.lang + .dir. */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {/* TODO(SEO C-05): replace /og-image.jpg with a real 1200x630 image asset (operator-provided artwork). */}
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={language === 'ar' ? 'ar_SA' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'ar' ? 'en_US' : 'ar_SA'} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={currentUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={imageUrl} />
      
      {/* Hreflang for multilingual SEO */}
      <link rel="alternate" hrefLang="ar" href={`${baseUrl}${currentPath}?lang=ar`} />
      <link rel="alternate" hrefLang="en" href={`${baseUrl}${currentPath}?lang=en`} />
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${currentPath}`} />
      
      {/* Canonical URL — query-stripped to avoid duplicate-content with ?lang= */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Structured Data / JSON-LD */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
